// "My Bookings" — authenticated customer view of their booking history.
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../lib/api';
import { useCustomerAuth } from '../../context/CustomerAuthContext';
import { useCurrency } from '../../context/CurrencyContext';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Building2, Plane, Bus, ChevronRight, FileText } from 'lucide-react';

const STATUS_COLOR = {
  confirmed: 'bg-emerald-100 text-emerald-700',
  pending_payment: 'bg-amber-100 text-amber-700',
  payment_captured: 'bg-emerald-100 text-emerald-700',
  cancelled: 'bg-gray-100 text-gray-700',
  payment_failed: 'bg-red-100 text-red-700',
  issuance_failed: 'bg-red-100 text-red-700',
};

const SERVICE_ICON = { hotels: Building2, flights: Plane, buses: Bus };

const MyBookings = () => {
  const { customer, loading: authLoading } = useCustomerAuth();
  const { formatPrice } = useCurrency();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('upcoming');

  useEffect(() => {
    if (!customer) { setLoading(false); return; }
    api.get('/customer/bookings')
      .then((r) => setBookings(r.data.bookings || []))
      .catch(() => setBookings([]))
      .finally(() => setLoading(false));
  }, [customer]);

  if (!authLoading && !customer) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-20 text-center">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Please sign in to view your bookings</h1>
        <p className="text-gray-600 dark:text-gray-400">Your booking history is linked to your Elite Travo account.</p>
      </div>
    );
  }

  const tabs = [
    { value: 'upcoming', label: 'Upcoming' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' },
  ];
  const filtered = bookings.filter((b) => {
    if (tab === 'cancelled') return b.status === 'cancelled';
    if (tab === 'completed') return ['confirmed', 'payment_captured'].includes(b.status) && _isPast(b);
    // upcoming = everything else with a confirmed/pending status and future date
    return !_isPast(b) && b.status !== 'cancelled';
  });

  return (
    <div className="bg-gray-50 dark:bg-gray-950 min-h-screen">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-1">My Bookings</h1>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">All your past and upcoming trips, in one place.</p>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 border-b border-gray-200 dark:border-gray-800">
          {tabs.map((t) => (
            <button
              key={t.value}
              type="button"
              onClick={() => setTab(t.value)}
              data-testid={`bookings-tab-${t.value}`}
              className={`px-4 py-2.5 text-sm font-semibold border-b-2 transition-colors ${
                tab === t.value
                  ? 'border-red-600 text-red-600 dark:text-red-400'
                  : 'border-transparent text-gray-500 hover:text-gray-800 dark:hover:text-gray-200'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => <div key={i} className="h-28 bg-white dark:bg-gray-900 rounded-xl shimmer" />)}
          </div>
        ) : filtered.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <FileText className="h-10 w-10 text-gray-400 mx-auto mb-3" />
              <p className="font-semibold text-gray-900 dark:text-white">No {tab} bookings</p>
              <p className="text-sm text-gray-500 mt-1">When you book a trip on Elite Travo it will appear here.</p>
              <Link to="/" className="inline-block mt-4 text-sm font-semibold text-red-600 hover:underline">Start exploring →</Link>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3" data-testid="bookings-list">
            {filtered.map((b) => {
              const Icon = SERVICE_ICON[b.service] || FileText;
              return (
                <Link key={b.id} to={`/account/bookings/${b.id}`} data-testid={`booking-card-${b.id}`}>
                  <Card className="hover:shadow-md transition-all duration-200 hover:border-red-200">
                    <CardContent className="p-4 sm:p-5 flex items-center gap-4">
                      <div className="bg-red-50 dark:bg-red-900/30 p-3 rounded-xl flex-shrink-0">
                        <Icon className="h-5 w-5 text-red-600 dark:text-red-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-bold text-gray-900 dark:text-white truncate">
                            {_title(b)}
                          </h3>
                          <Badge className={`${STATUS_COLOR[b.status] || 'bg-gray-100 text-gray-700'} text-[10px] uppercase`}>{b.status?.replace(/_/g, ' ')}</Badge>
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">
                          {_subtitle(b)} · ID {b.id}
                        </p>
                      </div>
                      <div className="text-right hidden sm:block">
                        <p className="font-bold text-gray-900 dark:text-white">{formatPrice(b.total_amount || 0)}</p>
                        <p className="text-xs text-gray-500">{new Date(b.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</p>
                      </div>
                      <ChevronRight className="h-4 w-4 text-gray-400 flex-shrink-0" />
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

const _title = (b) => {
  const snap = b.item_snapshot || {};
  if (b.service === 'hotels') return snap.name || 'Hotel booking';
  if (b.service === 'flights') return `${snap.airline || 'Flight'} ${snap.flight_number || ''}`.trim();
  if (b.service === 'buses') return snap.operator || 'Bus booking';
  return 'Booking';
};

const _subtitle = (b) => {
  const snap = b.item_snapshot || {};
  const extras = b.extras || {};
  if (b.service === 'hotels') {
    const where = [snap.area, snap.city || snap.destination_name].filter(Boolean).join(', ');
    return `${where || 'Hotel'} · ${extras.check_in || ''} → ${extras.check_out || ''}`.replace(/\s+/g, ' ').trim();
  }
  if (b.service === 'flights') return `${snap.from_code || ''} → ${snap.to_code || ''} · ${extras.departure_date || ''}`.trim();
  if (b.service === 'buses') return `${snap.from || ''} → ${snap.to || ''} · ${extras.travel_date || ''}`.trim();
  return '';
};

const _isPast = (b) => {
  const extras = b.extras || {};
  const dateStr = extras.check_out || extras.check_in || extras.departure_date || extras.travel_date || b.created_at;
  try {
    return new Date(dateStr) < new Date();
  } catch {
    return false;
  }
};

export default MyBookings;
